import type { AdminUserView } from "@webuild-ai/admin/models/components";

import React, { useMemo, useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tooltip,
  addToast,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { WorkflowOutput } from "@webuild-ai/admin/models/components";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useAdminStore } from "@/stores/AdminStore";
import { logger } from "@/telemetry";

import { WorkflowAccessPanel } from "./WorkflowAccessPanel";

interface WorkflowsTabProps {
  workflows: WorkflowOutput[];
  users: AdminUserView[];
  workflowUserCounts: Record<string, number>;
}

export const WorkflowsTab: React.FC<WorkflowsTabProps> = ({
  workflows,
  users,
  workflowUserCounts,
}) => {
  const { t } = useTranslation();
  const { adminClient } = useWebuildAPIClient();
  const { deleteWorkflow } = useAdminStore();

  const [search, setSearch] = useState("");
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowOutput | null>(null);
  const [deleteModal, setDeleteModal] = useState<WorkflowOutput | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const filteredWorkflows = useMemo(() => {
    if (!search) return workflows;
    const lower = search.toLowerCase();

    return workflows.filter(
      (w) => w.name?.toLowerCase().includes(lower) || w.id?.toLowerCase().includes(lower),
    );
  }, [workflows, search]);

  const handleDelete = async () => {
    if (!deleteModal || !adminClient) return;
    setIsDeleting(true);
    try {
      await deleteWorkflow(adminClient, deleteModal.id);
      addToast({
        title: t("Workflow deleted"),
        description: t("{{name}} has been removed", { name: deleteModal.name || deleteModal.id }),
        color: "success",
      });
      if (selectedWorkflow?.id === deleteModal.id) {
        setSelectedWorkflow(null);
      }
    } catch (error) {
      logger.error("Failed to delete workflow", error instanceof Error ? error : undefined);
      addToast({
        title: t("Error"),
        description: t("Failed to delete workflow. Please try again."),
        color: "danger",
      });
    } finally {
      setIsDeleting(false);
      setDeleteModal(null);
    }
  };

  return (
    <div className="flex gap-0 pt-4">
      {/* Workflows table — shrinks when panel is open */}
      <div className={`flex-1 min-w-0 space-y-4 transition-all ${selectedWorkflow ? "pr-4" : ""}`}>
        <Input
          className="max-w-md"
          placeholder={t("Search workflows...")}
          startContent={<Icon icon="solar:magnifer-outline" width={18} />}
          value={search}
          onValueChange={setSearch}
        />

        <Table
          aria-label={t("Workflows table")}
          selectionMode="single"
          onRowAction={(key) => {
            const wf = workflows.find((w) => w.id === String(key));

            if (wf) setSelectedWorkflow(wf);
          }}
        >
          <TableHeader>
            <TableColumn>{t("Workflow Name")}</TableColumn>
            <TableColumn>{t("Approved Users")}</TableColumn>
            <TableColumn>{t("Description")}</TableColumn>
            <TableColumn width={60}>{""}</TableColumn>
          </TableHeader>
          <TableBody emptyContent={t("No workflows found")} items={filteredWorkflows}>
            {(workflow) => (
              <TableRow key={workflow.id} className="cursor-pointer">
                <TableCell>{workflow.name || workflow.id}</TableCell>
                <TableCell className="text-default-400 text-sm">
                  {workflowUserCounts[workflow.id] ?? "—"}
                </TableCell>
                <TableCell className="text-default-400 text-sm truncate max-w-xs">
                  {workflow.description || "—"}
                </TableCell>
                <TableCell>
                  <Tooltip color="danger" content={t("Delete workflow")}>
                    <Button
                      isIconOnly
                      color="danger"
                      size="sm"
                      variant="light"
                      onPress={() => setDeleteModal(workflow)}
                    >
                      <Icon icon="solar:trash-bin-trash-outline" width={18} />
                    </Button>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Side panel for workflow access management */}
      {selectedWorkflow && (
        <WorkflowAccessPanel
          allUsers={users}
          workflow={selectedWorkflow}
          onClose={() => setSelectedWorkflow(null)}
        />
      )}

      {/* Delete confirmation modal */}
      <Modal isOpen={!!deleteModal} onClose={() => setDeleteModal(null)}>
        <ModalContent>
          <ModalHeader>{t("Delete Workflow")}</ModalHeader>
          <ModalBody>
            <p>
              {t('Are you sure you want to delete "{{name}}"?', {
                name: deleteModal?.name || deleteModal?.id,
              })}
            </p>
            <p className="text-danger text-sm mt-2">
              {t("This action cannot be undone. All user access to this workflow will be removed.")}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setDeleteModal(null)}>
              {t("Cancel")}
            </Button>
            <Button color="danger" isLoading={isDeleting} onPress={handleDelete}>
              {t("Delete")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};
